import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Radiation, IdCard, FileBadge, Save, Plus, Trash2, ShieldCheck, AlertTriangle,
  Activity, Sparkles, Edit2, X, Shield,
} from 'lucide-react';
import Header from '../components/Header';
import DatePickerField from '../components/DatePickerField';
import EvidenceUploads from '../components/EvidenceUploads';
import BulkActions from '../components/BulkActions';
import PDFIntakeDropzone from '../components/PDFIntakeDropzone';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = '#7C3AED';

export default function RadiationGovernancePage() {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [tab, setTab] = useState('overview');
  const [summary, setSummary] = useState(null);

  // ── Sync tab with URL hash so /radiation-governance#tld deep-links work ────
  useEffect(() => {
    const sync = () => {
      const h = (window.location.hash || '').replace(/^#/, '');
      if (h && ['overview', 'rso', 'epa', 'tld', 'lead-aprons'].includes(h)) {
        setTab(h);
      }
    };
    sync();
    window.addEventListener('hashchange', sync);
    return () => window.removeEventListener('hashchange', sync);
  }, []);

  const reload = async () => {
    const s = await axios.get(`${API}/radiation-governance/summary`, { withCredentials: true });
    setSummary(s.data);
  };
  useEffect(() => { reload(); }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="radgov-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-xs text-muted-foreground mb-4 inline-flex items-center gap-1" data-testid="radgov-breadcrumb">
          <Link to="/compliance" className="hover:text-foreground inline-flex items-center gap-1">
            <Shield className="w-3 h-3" /> Compliance
          </Link>
          <span>›</span>
          <span className="text-foreground font-semibold">Radiation Governance</span>
        </div>
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3" style={{ backgroundColor: `${C}1A`, color: C }}>
              <Radiation className="w-3.5 h-3.5" />
              <span className="text-[10px] font-bold uppercase tracking-wider">Radiation Governance · DIAS Phase C</span>
            </div>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-foreground">RSO, EPA licence & monitoring</h1>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
              The four governance documents QIP audits look for: Radiation Safety Officer appointment, state EPA Radiation Management Licence, personnel TLD/OSL monitoring, and lead apron / shielding integrity register.
            </p>
          </div>
        </div>

        {/* KPI strip */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="radgov-stats">
            <Kpi label="RSO appointed" value={summary.rso_appointed ? 'Yes' : 'No'} color={summary.rso_appointed ? '#16A34A' : '#DC2626'} sub={summary.rso_name} />
            <Kpi label="EPA licence" value={summary.epa_licence_no || '—'} color={summary.epa_expired ? '#DC2626' : summary.epa_licence_no ? '#16A34A' : '#64748B'} sub={summary.epa_expires_at ? `expires ${summary.epa_expires_at}` : 'not set'} />
            <Kpi label="TLD readings" value={summary.tld_readings_total} color="#0EA5E9" />
            <Kpi label="Lead aprons" value={summary.lead_aprons_total} color={summary.lead_aprons_failed > 0 ? '#DC2626' : '#16A34A'} sub={summary.lead_aprons_failed > 0 ? `${summary.lead_aprons_failed} failed` : 'all in service'} />
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-4 bg-card border border-border rounded-xl p-1 w-fit" data-testid="radgov-tabs">
          {[
            { k: 'overview', l: 'Overview' },
            { k: 'rso', l: 'RSO appointment' },
            { k: 'epa', l: 'EPA licence' },
            { k: 'tld', l: 'TLD / OSL register' },
            { k: 'lead-aprons', l: 'Lead aprons' },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`radgov-tab-${t.k}`}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
              style={tab === t.k ? { backgroundColor: `${C}15`, color: C } : { color: 'hsl(var(--muted-foreground))' }}>
              {t.l}
            </button>
          ))}
        </div>

        {tab === 'overview' && <OverviewPanel summary={summary} />}
        {tab === 'rso' && <RSOPanel isPM={isPM} onChange={reload} />}
        {tab === 'epa' && <EPAPanel isPM={isPM} onChange={reload} />}
        {tab === 'tld' && <TLDPanel isPM={isPM} onChange={reload} />}
        {tab === 'lead-aprons' && <ApronPanel isPM={isPM} onChange={reload} />}
      </div>
    </div>
  );
}

function Kpi({ label, value, color, sub }) {
  return (
    <div className="bg-card border border-border rounded-xl p-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold mt-1 truncate" style={{ color }}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{sub}</p>}
    </div>
  );
}

function OverviewPanel({ summary }) {
  if (!summary) return null;
  const items = [
    { ok: summary.rso_appointed, label: 'Radiation Safety Officer appointed' },
    { ok: !!summary.epa_licence_no && !summary.epa_expired, label: 'EPA Radiation Management Licence current' },
    { ok: summary.tld_readings_total > 0, label: 'Personnel TLD / OSL monitoring readings on file' },
    { ok: summary.lead_aprons_total > 0 && summary.lead_aprons_failed === 0, label: 'Lead aprons registered & passing integrity tests' },
  ];
  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-2" data-testid="radgov-overview">
      <h3 className="font-['Outfit'] text-lg font-semibold text-foreground mb-2">DIAS readiness checklist</h3>
      {items.map((i, idx) => (
        <div key={idx} className="flex items-center gap-2 text-sm">
          {i.ok
            ? <ShieldCheck className="w-4 h-4 text-green-600 shrink-0" />
            : <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />}
          <span className={i.ok ? 'text-foreground' : 'text-muted-foreground'}>{i.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RSO + EPA single-doc panels share a generic Form layout
// ─────────────────────────────────────────────────────────────────────────────

function RSOPanel({ isPM, onChange }) {
  const [d, setD] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = async () => {
    const r = await axios.get(`${API}/radiation-governance/rso`, { withCredentials: true });
    setD(r.data || {});
  };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setD(s => ({ ...s, [k]: v }));
  const save = async () => {
    if (!d.name?.trim()) { setMsg({ type: 'error', text: 'RSO name is required.' }); return; }
    setSaving(true); setMsg(null);
    try {
      await axios.put(`${API}/radiation-governance/rso`, d, { withCredentials: true });
      setMsg({ type: 'success', text: 'RSO appointment saved.' });
      onChange?.();
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Save failed.' }); }
    setSaving(false);
  };
  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="radgov-rso-panel">
      <div className="flex items-center gap-2 mb-3">
        <IdCard className="w-4 h-4" style={{ color: C }} />
        <h3 className="font-['Outfit'] text-lg font-semibold text-foreground">RSO appointment</h3>
      </div>
      {msg && <FormMsg msg={msg} />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Inp label="Full name *" value={d.name} onChange={v => set('name', v)} disabled={!isPM} testid="rso-name" />
        <Inp label="Role title" value={d.role || 'Radiation Safety Officer'} onChange={v => set('role', v)} disabled={!isPM} testid="rso-role" />
        <DateInp label="Appointed at" value={d.appointed_at} onChange={v => set('appointed_at', v)} disabled={!isPM} testid="rso-appointed" />
        <Inp label="Certificate #" value={d.certificate_number} onChange={v => set('certificate_number', v)} disabled={!isPM} testid="rso-cert" />
        <DateInp label="Certificate expiry" value={d.certificate_expiry} onChange={v => set('certificate_expiry', v)} disabled={!isPM} testid="rso-cert-exp" />
        <Inp label="Contact email" value={d.contact_email} onChange={v => set('contact_email', v)} disabled={!isPM} testid="rso-email" />
        <Inp label="Contact phone" value={d.contact_phone} onChange={v => set('contact_phone', v)} disabled={!isPM} testid="rso-phone" />
        <Inp label="Document URL" value={d.document_url} onChange={v => set('document_url', v)} disabled={!isPM} testid="rso-doc" />
        <div className="md:col-span-2">
          <Inp label="Notes" value={d.notes} onChange={v => set('notes', v)} disabled={!isPM} testid="rso-notes" type="textarea" />
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-border">
        <EvidenceUploads parentType="rso" parentId="rso" label="RSO appointment letter" testidPrefix="rso-evidence" />
      </div>
      {isPM && (
        <div className="flex justify-end mt-4">
          <button onClick={save} disabled={saving} data-testid="rso-save-btn"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-50"
            style={{ backgroundColor: C }}>
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save RSO appointment'}
          </button>
        </div>
      )}
    </div>
  );
}

function EPAPanel({ isPM, onChange }) {
  const [d, setD] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = async () => {
    const r = await axios.get(`${API}/radiation-governance/epa-licence`, { withCredentials: true });
    setD(r.data || {});
  };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setD(s => ({ ...s, [k]: v }));
  const save = async () => {
    if (!d.licence_number?.trim()) { setMsg({ type: 'error', text: 'Licence number is required.' }); return; }
    setSaving(true); setMsg(null);
    try {
      await axios.put(`${API}/radiation-governance/epa-licence`, d, { withCredentials: true });
      setMsg({ type: 'success', text: 'EPA licence saved.' });
      onChange?.();
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Save failed.' }); }
    setSaving(false);
  };
  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="radgov-epa-panel">
      <div className="flex items-center gap-2 mb-3">
        <FileBadge className="w-4 h-4" style={{ color: C }} />
        <h3 className="font-['Outfit'] text-lg font-semibold text-foreground">EPA Radiation Management Licence</h3>
      </div>
      {msg && <FormMsg msg={msg} />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Inp label="Licence number *" value={d.licence_number} onChange={v => set('licence_number', v)} disabled={!isPM} testid="epa-no" />
        <Inp label="Issuing authority" value={d.issuing_authority || 'NSW Environment Protection Authority'} onChange={v => set('issuing_authority', v)} disabled={!isPM} testid="epa-authority" />
        <Inp label="Scope" value={d.scope} onChange={v => set('scope', v)} disabled={!isPM} testid="epa-scope" />
        <DateInp label="Issued at" value={d.issued_at} onChange={v => set('issued_at', v)} disabled={!isPM} testid="epa-issued" />
        <DateInp label="Expires at" value={d.expires_at} onChange={v => set('expires_at', v)} disabled={!isPM} testid="epa-expires" />
        <Inp label="Document URL" value={d.document_url} onChange={v => set('document_url', v)} disabled={!isPM} testid="epa-doc" />
        <div className="md:col-span-2">
          <Inp label="Notes" value={d.notes} onChange={v => set('notes', v)} disabled={!isPM} testid="epa-notes" type="textarea" />
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-border">
        <EvidenceUploads parentType="epa" parentId="epa" label="EPA radiation licence PDF" testidPrefix="epa-evidence" />
      </div>
      {isPM && (
        <div className="flex justify-end mt-4">
          <button onClick={save} disabled={saving} data-testid="epa-save-btn"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-50"
            style={{ backgroundColor: C }}>
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save EPA licence'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TLD readings list + inline form
// ─────────────────────────────────────────────────────────────────────────────

function TLDPanel({ isPM, onChange }) {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = async () => {
    const r = await axios.get(`${API}/radiation-governance/tld`, { withCredentials: true });
    setRows(r.data);
  };
  useEffect(() => { if (isPM) load(); }, [isPM]);
  if (!isPM) {
    return <div className="bg-card border border-border rounded-2xl p-5 text-sm text-muted-foreground">Restricted to Practice Manager / admin.</div>;
  }
  const onSave = async (form) => {
    await axios.post(`${API}/radiation-governance/tld`, form, { withCredentials: true });
    setShowForm(false); load(); onChange?.();
  };
  const onDelete = async (id) => {
    if (!window.confirm('Delete this reading?')) return;
    await axios.delete(`${API}/radiation-governance/tld/${id}`, { withCredentials: true });
    load(); onChange?.();
  };
  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="radgov-tld-panel">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4" style={{ color: C }} />
          <h3 className="font-['Outfit'] text-lg font-semibold text-foreground">Personnel TLD / OSL readings</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <BulkActions resource="tld_readings" title="TLD readings" color={C} onAfterImport={load} />
          <button onClick={() => setShowForm(s => !s)} data-testid="tld-add-btn"
            className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg text-white"
            style={{ backgroundColor: C }}>
            <Plus className="w-3 h-3" /> {showForm ? 'Close' : 'Add reading'}
          </button>
        </div>
      </div>
      {showForm && <TLDForm onSubmit={onSave} onCancel={() => setShowForm(false)} />}
      {rows.length === 0
        ? <p className="text-xs text-muted-foreground italic py-3">No personnel monitoring readings yet.</p>
        : <div className="space-y-1.5 mt-3" data-testid="tld-list">
          {rows.map(r => (
            <div key={r.reading_id} className="bg-muted/40 rounded-lg p-3 text-xs flex items-center gap-3 justify-between flex-wrap">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-semibold text-foreground">{r.user_name}</span>
                <span className="text-muted-foreground font-mono">Badge {r.badge_id}</span>
                <span className="text-muted-foreground">{r.period_start} → {r.period_end}</span>
                <span className="font-semibold" style={{ color: C }}>Hp(10): {r.deep_dose_msv ?? '—'} mSv</span>
                {r.shallow_dose_msv != null && <span className="text-muted-foreground">Hp(0.07): {r.shallow_dose_msv} mSv</span>}
                {r.eye_lens_dose_msv != null && <span className="text-muted-foreground">Eye: {r.eye_lens_dose_msv} mSv</span>}
                {r.provider && <span className="text-muted-foreground">{r.provider}</span>}
              </div>
              <button onClick={() => onDelete(r.reading_id)} className="p-1 text-muted-foreground hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>}
    </div>
  );
}

function TLDForm({ onSubmit, onCancel }) {
  const [f, setF] = useState({ user_name: '', badge_id: '', period_start: '', period_end: '', deep_dose_msv: '', shallow_dose_msv: '', eye_lens_dose_msv: '', readout_at: '', provider: 'ARPANSA Personal Radiation Monitoring Service', notes: '' });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  return (
    <div className="border border-border rounded-lg p-3 space-y-2 bg-muted/30 mb-3" data-testid="tld-form">
      <PDFIntakeDropzone intakeType="tld_reading"
        label="Drop ARPANSA / OSL quarterly report — AI auto-fills"
        color={C} testidPrefix="tld-pdf-intake"
        onExtracted={(ex) => setF(s => ({
          ...s,
          user_name:        ex.user_name        || s.user_name,
          badge_id:         ex.badge_id         || s.badge_id,
          period_start:     ex.period_start     || s.period_start,
          period_end:       ex.period_end       || s.period_end,
          deep_dose_msv:    ex.deep_dose_msv    !== undefined && ex.deep_dose_msv !== '' ? String(ex.deep_dose_msv) : s.deep_dose_msv,
          shallow_dose_msv: ex.shallow_dose_msv !== undefined && ex.shallow_dose_msv !== '' ? String(ex.shallow_dose_msv) : s.shallow_dose_msv,
          eye_lens_dose_msv: ex.eye_lens_dose_msv !== undefined && ex.eye_lens_dose_msv !== '' ? String(ex.eye_lens_dose_msv) : s.eye_lens_dose_msv,
          provider:         ex.provider         || s.provider,
          notes:            ex.notes            || s.notes,
        }))} />
      <div className="grid grid-cols-2 gap-2">
        <Inp label="Staff name *" value={f.user_name} onChange={v => set('user_name', v)} testid="tld-name" />
        <Inp label="Badge ID *" value={f.badge_id} onChange={v => set('badge_id', v)} testid="tld-badge" />
        <DateInp label="Period start" value={f.period_start} onChange={v => set('period_start', v)} testid="tld-start" />
        <DateInp label="Period end" value={f.period_end} onChange={v => set('period_end', v)} testid="tld-end" />
        <Inp label="Hp(10) mSv" value={f.deep_dose_msv} onChange={v => set('deep_dose_msv', v)} testid="tld-deep" />
        <Inp label="Hp(0.07) mSv" value={f.shallow_dose_msv} onChange={v => set('shallow_dose_msv', v)} testid="tld-shallow" />
        <Inp label="Eye lens mSv" value={f.eye_lens_dose_msv} onChange={v => set('eye_lens_dose_msv', v)} testid="tld-eye" />
        <DateInp label="Readout at" value={f.readout_at} onChange={v => set('readout_at', v)} testid="tld-readout" />
        <div className="col-span-2"><Inp label="Provider" value={f.provider} onChange={v => set('provider', v)} testid="tld-provider" /></div>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="text-xs px-3 py-1.5 rounded-lg border border-border">Cancel</button>
        <button data-testid="tld-save-btn" onClick={() => {
          const payload = { ...f };
          ['deep_dose_msv', 'shallow_dose_msv', 'eye_lens_dose_msv'].forEach(k => {
            payload[k] = payload[k] === '' ? null : Number(payload[k]);
          });
          if (!payload.user_name.trim() || !payload.badge_id.trim() || !payload.period_start || !payload.period_end) return;
          onSubmit(payload);
        }} className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white" style={{ backgroundColor: C }}>Save reading</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Lead apron register
// ─────────────────────────────────────────────────────────────────────────────

function ApronPanel({ isPM, onChange }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => {
    const r = await axios.get(`${API}/radiation-governance/lead-aprons`, { withCredentials: true });
    setRows(r.data);
  };
  useEffect(() => { load(); }, []);
  const onSave = async (form) => {
    if (editing && editing !== 'new') {
      await axios.put(`${API}/radiation-governance/lead-aprons/${editing.apron_id}`, form, { withCredentials: true });
    } else {
      await axios.post(`${API}/radiation-governance/lead-aprons`, form, { withCredentials: true });
    }
    setEditing(null); load(); onChange?.();
  };
  const onDelete = async (id) => {
    if (!window.confirm('Delete this apron from the register?')) return;
    await axios.delete(`${API}/radiation-governance/lead-aprons/${id}`, { withCredentials: true });
    load(); onChange?.();
  };
  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="radgov-apron-panel">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" style={{ color: C }} />
          <h3 className="font-['Outfit'] text-lg font-semibold text-foreground">Lead apron / shielding register</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isPM && <BulkActions resource="lead_aprons" title="Lead apron register" color={C} onAfterImport={load} />}
          {isPM && <button onClick={() => setEditing('new')} data-testid="apron-add-btn"
            className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg text-white"
            style={{ backgroundColor: C }}>
            <Plus className="w-3 h-3" /> Add apron
          </button>}
        </div>
      </div>
      {rows.length === 0
        ? <p className="text-xs text-muted-foreground italic py-3">No aprons registered yet.</p>
        : <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="apron-list">
          {rows.map(r => (
            <div key={r.apron_id} className="bg-muted/40 rounded-lg p-3 text-xs">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-foreground">{r.apron_id} <span className="text-muted-foreground">· {r.type.replace('_', ' ')}</span></p>
                  {r.location && <p className="text-muted-foreground">{r.location}</p>}
                </div>
                {isPM && <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => setEditing(r)} className="p-1 text-muted-foreground hover:text-foreground"><Edit2 className="w-3 h-3" /></button>
                  <button onClick={() => onDelete(r.apron_id)} className="p-1 text-muted-foreground hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                </div>}
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 mt-2 text-[11px]">
                {r.last_integrity_test_at && <div><span className="text-muted-foreground">Last test:</span> {r.last_integrity_test_at}</div>}
                {r.next_integrity_test_due && <div><span className="text-muted-foreground">Next due:</span> {r.next_integrity_test_due}</div>}
                {r.test_result && <div className="col-span-2"><span className="text-muted-foreground">Result:</span> <span className={`font-semibold ${r.test_result === 'pass' ? 'text-green-600' : r.test_result === 'fail' ? 'text-red-600' : 'text-amber-600'}`}>{r.test_result}</span> via {r.test_method}</div>}
              </div>
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded mt-2 inline-block"
                style={{
                  backgroundColor: r.status === 'in_service' ? '#16A34A15' : r.status === 'failed' ? '#DC262615' : '#64748B15',
                  color: r.status === 'in_service' ? '#16A34A' : r.status === 'failed' ? '#DC2626' : '#64748B',
                }}>
                {r.status.replace('_', ' ')}
              </span>
            </div>
          ))}
        </div>}
      {editing && <ApronEditor initial={editing === 'new' ? null : editing} onSubmit={onSave} onCancel={() => setEditing(null)} />}
    </div>
  );
}

function ApronEditor({ initial, onSubmit, onCancel }) {
  const [f, setF] = useState(initial || { apron_id: '', type: 'lead_apron', location: '', purchase_date: '', last_integrity_test_at: '', next_integrity_test_due: '', test_result: '', test_method: 'fluoroscopic', notes: '', status: 'in_service' });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-background rounded-2xl border border-border w-full max-w-xl max-h-[90vh] overflow-y-auto p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold">{initial ? 'Edit apron' : 'New apron'}</h3>
          <button onClick={onCancel}><X className="w-4 h-4" /></button>
        </div>
        {!initial && (
          <PDFIntakeDropzone intakeType="lead_apron_test"
            label="Drop apron test report — AI auto-fills"
            color={C} testidPrefix="apron-pdf-intake"
            onExtracted={(ex) => setF(s => ({
              ...s,
              apron_id:                ex.apron_id    || s.apron_id,
              type:                    ex.type        || s.type,
              last_integrity_test_at:  ex.test_date   || s.last_integrity_test_at,
              test_method:             ex.test_method || s.test_method,
              test_result:             ex.test_result || s.test_result,
              next_integrity_test_due: ex.next_due    || s.next_integrity_test_due,
              notes:                   ex.notes       || s.notes,
            }))} />
        )}
        <div className="grid grid-cols-2 gap-2 mt-3">
          <Inp label="Apron ID *" value={f.apron_id} onChange={v => set('apron_id', v)} testid="apron-id" />
          <FSel label="Type" value={f.type} onChange={v => set('type', v)} options={['lead_apron', 'thyroid_shield', 'gonad_shield', 'gloves', 'glasses']} testid="apron-type" />
          <Inp label="Location" value={f.location} onChange={v => set('location', v)} testid="apron-loc" />
          <DateInp label="Purchase date" value={f.purchase_date} onChange={v => set('purchase_date', v)} testid="apron-purchase" />
          <DateInp label="Last integrity test" value={f.last_integrity_test_at} onChange={v => set('last_integrity_test_at', v)} testid="apron-last-test" />
          <DateInp label="Next test due" value={f.next_integrity_test_due} onChange={v => set('next_integrity_test_due', v)} testid="apron-next-test" />
          <FSel label="Test result" value={f.test_result} onChange={v => set('test_result', v)} options={['', 'pass', 'fail', 'pass_with_observation']} testid="apron-result" />
          <FSel label="Test method" value={f.test_method} onChange={v => set('test_method', v)} options={['fluoroscopic', 'radiographic', 'visual']} testid="apron-method" />
          <FSel label="Status" value={f.status} onChange={v => set('status', v)} options={['in_service', 'failed', 'retired']} testid="apron-status" />
          <div className="col-span-2"><Inp label="Notes" value={f.notes} onChange={v => set('notes', v)} testid="apron-notes" type="textarea" /></div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onCancel} className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
          <button data-testid="apron-save-btn" onClick={() => f.apron_id.trim() && onSubmit(f)}
            className="text-sm font-semibold px-4 py-1.5 rounded-lg text-white" style={{ backgroundColor: C }}>
            <Save className="w-3.5 h-3.5 inline-block mr-1" /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared form helpers
// ─────────────────────────────────────────────────────────────────────────────

function Inp({ label, value, onChange, testid, disabled, type = 'text' }) {
  const cls = "w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2 disabled:opacity-60";
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      {type === 'textarea'
        ? <textarea value={value || ''} disabled={disabled} onChange={e => onChange(e.target.value)} data-testid={testid} className={cls} rows={2} />
        : <input type="text" value={value ?? ''} disabled={disabled} onChange={e => onChange(e.target.value)} data-testid={testid} className={cls} />}
    </label>
  );
}

function DateInp({ label, value, onChange, testid, disabled }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <DatePickerField value={value} onChange={onChange} format="iso" testid={testid} disabled={disabled} />
    </label>
  );
}

function FSel({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <select value={value || ''} onChange={e => onChange(e.target.value)} data-testid={testid}
        className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2">
        {options.map(o => <option key={o} value={o}>{o || '—'}</option>)}
      </select>
    </label>
  );
}

function FormMsg({ msg }) {
  return (
    <div className={`rounded-lg border p-2.5 text-xs mb-3 flex items-start gap-2 ${msg.type === 'success' ? 'bg-green-500/5 border-green-500/30 text-green-600' : 'bg-red-500/5 border-red-500/30 text-red-600'}`}>
      {msg.type === 'success' ? <Sparkles className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />} {msg.text}
    </div>
  );
}
